import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import BookingConfirmation from "@shared/cards/bookingConfirmed";
import {
  getBookingWaitStatus,
  getTransactionByReference,
} from "@services/krent.service";

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function transformTransactionToBookingData(transactionData: any, ref: string) {
  const arrivalDate = transactionData.arrival_date
    ? new Date(transactionData.arrival_date)
    : null;
  const departureDate = transactionData.departure_date
    ? new Date(transactionData.departure_date)
    : null;

  let numberOfDays = 1;
  if (arrivalDate && departureDate) {
    const diffTime = Math.abs(departureDate.getTime() - arrivalDate.getTime());
    numberOfDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  const bill = transactionData.bill?.[0] || {};
  const propertyPrice = bill.propertyPrice || 0;
  const cautionFee =
    bill.cautionFee || transactionData.listing?.cautionFee || 0;
  const serviceCharge =
    bill.serviceCharge || transactionData.listing?.service_charge || 0;

  const pricePerNight = transactionData.usePartyPrice
    ? transactionData.listing?.partyPrice || propertyPrice
    : transactionData.listing?.price || propertyPrice;

  return {
    id: transactionData.id || transactionData._id,
    status: transactionData.status || "paid_pending_confirmation",
    paymentMethod: transactionData.paymentMethod,
    adminProofVerified: transactionData.adminProofVerified ?? false,
    hostAcceptedBankTransfer: transactionData.hostAcceptedBankTransfer ?? false,
    bookingFlow: transactionData.bookingFlow,
    paymentProof: transactionData.paymentProof,
    referenceId: transactionData.referenceId || ref,
    propertyName: transactionData.listing?.title || "Property Name",
    propertyImage:
      transactionData.listing?.coverImage ||
      transactionData.listing?.otherImages?.[0] ||
      null,
    propertyRef: transactionData.listing?.referenceNumber || "",
    propertyId: transactionData.listing?.id || "",
    rating: transactionData.listing?.averageRating || 0,
    reviewCount: transactionData.listing?.reviewsCount || 0,
    dateFrom: arrivalDate,
    dateTo: departureDate,
    guests: {
      adults: transactionData.guest?.adult || 1,
      children: transactionData.guest?.children || 0,
      infants: transactionData.guest?.infants || 0,
      pets: transactionData.guest?.pets || 0,
    },
    pricePerNight: pricePerNight,
    numberOfDays: numberOfDays,
    cautionFee: cautionFee,
    serviceFee: serviceCharge,
    currency: transactionData.listing?.currency === "NGN" ? "₦" : "$",
    owner: transactionData.owner,
    sessionExpiresAt: transactionData.sessionExpiresAt,
    instantBooking: transactionData.listing?.instantBooking || false,
  };
}

async function confirmPaystackIfNeeded(transactionData: any) {
  const transactionId = transactionData.id || transactionData._id;
  if (
    !transactionId ||
    transactionData.bookingFlow !== "request" ||
    transactionData.status !== "pending_payment" ||
    transactionData.paymentMethod === "manual_bank_transfer"
  ) {
    return transactionData;
  }

  let latest = transactionData;
  for (let attempt = 0; attempt < 8; attempt += 1) {
    try {
      const wait = await getBookingWaitStatus(transactionId);
      if (wait?.status) {
        latest = {
          ...latest,
          status: wait.status,
          bookingReference: wait.bookingReference,
          sessionExpiresAt: wait.sessionExpiresAt ?? latest.sessionExpiresAt,
        };
        if (wait.status !== "pending_payment") {
          return latest;
        }
      }
    } catch {
      // Keep retrying — webhook or Paystack may still be catching up
    }
    await sleep(1500);
  }

  return latest;
}

const BookingConfirm: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentReference, setPaymentReference] = useState<string>("");
  const [bookingData, setBookingData] = useState<any>(null);

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      const searchParams = new URLSearchParams(location.search);
      const trxref = searchParams.get("trxref");
      const reference = searchParams.get("reference");
      const ref = trxref || reference || "";
      setPaymentReference(ref);

      if (!ref) {
        return;
      }

      try {
        const transactionResponse = await getTransactionByReference(ref);
        const transactionData = (transactionResponse as any)?.data;

        if (transactionData) {
          // Long-lease: redirect to the booking detail page so it renders
          // its own "Payment Confirmed" screen with the full transaction context.
          const isLongLease = transactionData.listing?.intent === "long lease";
          if (isLongLease) {
            const transactionId =
              transactionData.id || transactionData._id || "";
            navigate(
              `/my-bookings/${transactionId}?trxref=${encodeURIComponent(ref)}`,
              { replace: true },
            );
            return;
          }

          const confirmed = await confirmPaystackIfNeeded(transactionData);
          setBookingData(transformTransactionToBookingData(confirmed, ref));
        }
      } catch (error) {
        const storedBookingData = sessionStorage.getItem("bookingData");
        if (storedBookingData) {
          try {
            const parsed = JSON.parse(storedBookingData);
            const confirmed = await confirmPaystackIfNeeded(parsed);
            setBookingData(confirmed);
          } catch (parseError) {
            console.error("Error parsing stored booking data:", parseError);
          }
        }
      }
    };

    fetchTransactionDetails();
  }, [location.search, navigate]);

  return (
    <BookingConfirmation
      bookingData={bookingData}
      paymentReference={paymentReference}
    />
  );
};

export default BookingConfirm;
